// The queue between "the user added something" and "the shared catalogue hears about it". Adding
// never waits on the network: a submission is written to localStorage first and sent whenever the
// device is online, retried with growing delays, and forgotten once the edge has it. The same thing
// queued twice is queued once, and a thing the edge already confirmed is not sent again.
import type { NewSubmission } from "@proef/contract";
import { useSyncExternalStore } from "react";
import { z } from "zod";
import { isEdgeEnabled } from "../backends";
import { foldKey } from "../catalogue/fold";
import { call, EdgeApiError } from "../edgeApi";
import { forgetSubmittedGtin, markBarcodeAccepted } from "./preferences";
import { sanitise, type SubmissionDraft } from "./submissions";

export interface OutboxItem {
  readonly id: string;
  /** What makes two submissions the same thing; see {@link dedupeKey}. */
  readonly key: string;
  readonly submission: NewSubmission;
  readonly attempts: number;
  /** Epoch ms before which a flush leaves it alone. */
  readonly nextAttemptAt: number;
  readonly createdAt: number;
}

interface Stored {
  items: OutboxItem[];
  /** Keys the edge confirmed, newest last, so a re-queued duplicate costs no request. */
  sent: string[];
}

const STORAGE_KEY = "proef.outbox";
const MAX_ITEMS = 200;
const MAX_SENT = 500;
/** An item still unsent after this long is stale: the user has long since moved on. */
const MAX_AGE_MS = 30 * 86_400_000;
const BASE_DELAY_MS = 30_000;
const MAX_DELAY_MS = 6 * 3_600_000;
const RATE_LIMIT_DELAY_MS = 3_600_000;

const Received = z.object({ status: z.literal("received") });

/** The edge's own notion of sameness: kind plus the folded facts that identify the thing. */
export function dedupeKey(submission: NewSubmission): string {
  if (submission.kind === "producer") return `producer:${foldKey(submission.name)}`;
  if (submission.kind === "wine") {
    return `wine:${foldKey(submission.producer)}|${foldKey(submission.name)}|${String(submission.vintage ?? "")}`;
  }
  return `barcode:${submission.gtin.padStart(14, "0")}`;
}

/** Seconds-free exponential backoff: 30 s, 1 min, 2 min, … capped at six hours. */
export function backoffMs(attempts: number): number {
  return Math.min(BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1), MAX_DELAY_MS);
}

function isItem(value: unknown): value is OutboxItem {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string" &&
    "key" in value &&
    typeof value.key === "string" &&
    "submission" in value &&
    typeof value.submission === "object" &&
    value.submission !== null &&
    "attempts" in value &&
    typeof value.attempts === "number" &&
    "nextAttemptAt" in value &&
    typeof value.nextAttemptAt === "number" &&
    "createdAt" in value &&
    typeof value.createdAt === "number"
  );
}

function read(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { items: [], sent: [] };
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== "object" || stored === null) return { items: [], sent: [] };
    const items =
      "items" in stored && Array.isArray(stored.items) ? stored.items.filter(isItem) : [];
    const sent =
      "sent" in stored && Array.isArray(stored.sent)
        ? stored.sent.filter((k): k is string => typeof k === "string")
        : [];
    return { items, sent };
  } catch {
    return { items: [], sent: [] };
  }
}

let state: Stored = typeof window === "undefined" ? { items: [], sent: [] } : read();
const listeners = new Set<() => void>();

function commit(next: Stored): void {
  state = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked or full: the queue lasts this session.
  }
  listeners.forEach((l) => {
    l();
  });
}

/** Everything still waiting to be sent, oldest first. */
export function pendingSubmissions(): readonly OutboxItem[] {
  return state.items;
}

export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How many submissions are waiting, for About to show. */
export function usePendingCount(): number {
  return useSyncExternalStore(
    subscribeOutbox,
    () => state.items.length,
    () => 0,
  );
}

type WineSubmission = Extract<NewSubmission, { kind: "wine" }>;

const stated = (vintage: number | null | undefined): vintage is number =>
  typeof vintage === "number";

/**
 * The same wine under two spellings of the facts: producer and name agree once folded, and the
 * vintages agree or one of them is not stated (a wine added by name only, then again with a year).
 */
function sameWine(a: WineSubmission, b: WineSubmission): boolean {
  return (
    foldKey(a.producer) === foldKey(b.producer) &&
    foldKey(a.name) === foldKey(b.name) &&
    (!stated(a.vintage) || !stated(b.vintage) || a.vintage === b.vintage)
  );
}

/** One wine from two submissions of it: whatever either one knew, with `newer` winning where both did. */
function mergeWine(older: WineSubmission, newer: WineSubmission): WineSubmission {
  const vintage = newer.vintage ?? older.vintage;
  const country = newer.country ?? older.country;
  const colour = newer.colour ?? older.colour;
  const manualWineId = newer.manualWineId ?? older.manualWineId;
  return {
    kind: "wine",
    producer: newer.producer,
    name: newer.name,
    ...(manualWineId === undefined ? {} : { manualWineId }),
    ...(stated(vintage) ? { vintage } : {}),
    ...(country === undefined ? {} : { country }),
    ...(colour === undefined ? {} : { colour }),
    sparkling: newer.sparkling || older.sparkling,
    grapes: newer.grapes.length > 0 ? newer.grapes : older.grapes,
  };
}

/**
 * A wine the edge already has: the same wine as {@link sameWine} reads it, so one typed first by
 * name only and then again with a year (or the other way round) is not a second request.
 */
function sentWine(submission: NewSubmission): boolean {
  if (submission.kind !== "wine") return false;
  const prefix = `wine:${foldKey(submission.producer)}|${foldKey(submission.name)}|`;
  const year = stated(submission.vintage) ? String(submission.vintage) : "";
  return state.sent.some((key) => {
    if (!key.startsWith(prefix)) return false;
    const sentYear = key.slice(prefix.length);
    return year === "" || sentYear === "" || sentYear === year;
  });
}

/** The unsent item already standing for this wine, if any. */
function queuedWine(wine: WineSubmission): OutboxItem | undefined {
  return state.items.find(
    (item) => item.submission.kind === "wine" && sameWine(item.submission, wine),
  );
}

/**
 * Queues a submission and tries to send it at once (a no-op offline). Returns whether it was
 * newly queued: nothing to say, already queued, or already confirmed by the edge all answer false.
 * A wine already waiting under another spelling of its facts is enriched in place, not queued twice.
 */
export function enqueue(raw: SubmissionDraft): boolean {
  const submission = sanitise(raw);
  if (submission === undefined) return false;
  const key = dedupeKey(submission);
  if (state.sent.includes(key) || sentWine(submission)) return false;
  const waiting =
    submission.kind === "wine"
      ? queuedWine(submission)
      : state.items.find((item) => item.key === key);
  if (waiting !== undefined) {
    if (submission.kind === "wine" && waiting.submission.kind === "wine") {
      const merged = mergeWine(waiting.submission, submission);
      commit({
        ...state,
        items: state.items.map((item) =>
          item.id === waiting.id ? { ...item, key: dedupeKey(merged), submission: merged } : item,
        ),
      });
    }
    return false;
  }
  const now = Date.now();
  const item: OutboxItem = {
    id: crypto.randomUUID(),
    key,
    submission,
    attempts: 0,
    nextAttemptAt: now,
    createdAt: now,
  };
  commit({ ...state, items: [...state.items, item].slice(-MAX_ITEMS) });
  void flushOutbox();
  return true;
}

/**
 * The wine reached the catalogue's reviewers by another route (a proposal): drops it from the queue
 * and remembers it as sent, so it is not submitted a second time from here.
 */
export function withdrawWine(draft: SubmissionDraft): void {
  const wine = sanitise(draft);
  if (wine?.kind !== "wine") return;
  const key = dedupeKey(wine);
  commit({
    items: state.items.filter(
      (item) => item.submission.kind !== "wine" || !sameWine(item.submission, wine),
    ),
    sent: [...state.sent.filter((k) => k !== key), key].slice(-MAX_SENT),
  });
}

/** How a refused submission should be handled: a request that can never succeed is dropped. */
function isPermanent(err: unknown): boolean {
  return err instanceof EdgeApiError && [400, 413, 422].includes(err.status);
}

/** Tells the barcode memory how a barcode submission ended: accepted by the edge, or gone unsent. */
function settleBarcode(accepted: boolean): (item: OutboxItem) => void {
  return (item) => {
    if (item.submission.kind !== "barcode") return;
    if (accepted) markBarcodeAccepted(item.submission.gtin);
    else forgetSubmittedGtin(item.submission.gtin);
  };
}

let flushing: Promise<void> | undefined;

async function sendOne(item: OutboxItem): Promise<"sent" | "dropped" | "retry"> {
  try {
    await call(Received, "/v1/submissions", {
      method: "POST",
      body: JSON.stringify(item.submission),
    });
    return "sent";
  } catch (err) {
    if (isPermanent(err)) return "dropped";
    const attempts = item.attempts + 1;
    const wait =
      err instanceof EdgeApiError && err.status === 429
        ? Math.max(backoffMs(attempts), RATE_LIMIT_DELAY_MS)
        : backoffMs(attempts);
    retryLater(item.id, attempts, Date.now() + wait);
    return "retry";
  }
}

function retryLater(id: string, attempts: number, nextAttemptAt: number): void {
  commit({
    ...state,
    items: state.items.map((i) => (i.id === id ? { ...i, attempts, nextAttemptAt } : i)),
  });
}

async function run(force: boolean): Promise<void> {
  if (!isEdgeEnabled() || (typeof navigator !== "undefined" && !navigator.onLine)) return;
  const now = Date.now();
  const fresh = state.items.filter((i) => now - i.createdAt < MAX_AGE_MS);
  if (fresh.length !== state.items.length) {
    // A barcode that went stale unsent is no longer pending: its wine may be asked about again.
    state.items.filter((i) => !fresh.includes(i)).forEach(settleBarcode(false));
    commit({ ...state, items: fresh });
  }
  for (const item of state.items.filter((i) => force || i.nextAttemptAt <= now)) {
    const outcome = await sendOne(item);
    if (outcome === "retry") continue;
    settleBarcode(outcome === "sent")(item);
    commit({
      items: state.items.filter((i) => i.id !== item.id),
      sent: outcome === "sent" ? [...state.sent, item.key].slice(-MAX_SENT) : state.sent,
    });
  }
}

/**
 * Sends what is due (everything with `force`), one at a time, and never twice at once. Failures
 * are not thrown: they only reschedule the item.
 */
export function flushOutbox(force = false): Promise<void> {
  flushing ??= run(force).finally(() => {
    flushing = undefined;
    scheduleNext();
  });
  return flushing;
}

let timer: ReturnType<typeof setTimeout> | undefined;
let running = false;

function scheduleNext(): void {
  clearTimeout(timer);
  timer = undefined;
  if (!running || state.items.length === 0) return;
  const next = Math.min(...state.items.map((i) => i.nextAttemptAt));
  timer = setTimeout(
    () => {
      void flushOutbox();
    },
    Math.max(1000, next - Date.now()),
  );
}

const onOnline = (): void => {
  void flushOutbox(true);
};

/** Starts sending: now, whenever the device comes back online, and when a retry falls due. */
export function startOutbox(): () => void {
  running = true;
  window.addEventListener("online", onOnline);
  void flushOutbox();
  scheduleNext();
  return () => {
    running = false;
    clearTimeout(timer);
    window.removeEventListener("online", onOnline);
  };
}

/** Test seam: forget the queue and the sent list. */
export function resetOutbox(): void {
  state = { items: [], sent: [] };
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  listeners.forEach((l) => {
    l();
  });
}

/** Test seam: re-read storage as a fresh page load would. */
export function reloadOutbox(): void {
  state = read();
  listeners.forEach((l) => {
    l();
  });
}
